import { Check, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  countryCodes,
  countryLabel,
  durationInWeeks,
  hasChildren,
  outlineNumbers,
  subPhaseSpan,
  subtreeEnd,
  type CountryCode,
  type Phase,
  type ProjectBasicsPatch,
} from "@gb/contracts";
import { SectionLabel } from "@/components/gb/section-label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SpanLine } from "../phase-card";
import { useFoldedGroups } from "../phase-outline";
import { useJurisdiction, usePhases, useReplacePhases, useRequirements } from "../phases-api";
import { useActivateProject, useUpdateProject } from "../api";
import { RoleCards } from "./step-compliance";
import { STEP, StepPanel, WizardFooter, type StepProps } from "./wizard";

/** No phases yet: one array, so the fold hook is not handed a new one each render. */
const NO_PHASES: Phase[] = [];
const phaseKey = (phase: Phase): string => phase.id;

/** Debounce for in-place edits, per the design's "saves quietly" behaviour. */
const SAVE_DELAY_MS = 500;

/**
 * Every `DebouncedInput` on screen registers a flush here.
 *
 * The footer's primary can be pressed while a debounce is still counting down —
 * a keyboard user typing into a field and hitting Enter never blurs it — and
 * leaving with that timer unfired drops the edit. Nothing else reads this: it
 * exists so one control can say "write whatever is pending" without every field
 * being lifted into the step's state.
 *
 * A flush resolves `false` when its write failed, so the caller can decline to
 * leave. It never rejects: one failed field must not stop the others flushing.
 */
const FlushRegistry = createContext<Set<() => Promise<boolean>> | null>(null);

/**
 * The last step — review.
 *
 * Every field here is editable in place rather than being a read-only summary
 * with "go back to step 2" links, which is the requirement. Edits save on a
 * debounce and show a small "Saved"; a failure blocks the exit until it is
 * resolved, because leaving with an unsaved change would silently discard it.
 *
 * The footer's primary is the only thing that differs between the two modes:
 * setup activates and shows that the project is set up, edit flushes and
 * returns to the project. Everything else — all three sections and every
 * in-place edit — is identical.
 *
 * It has no Documents section since 2026-10-03, at the owner's call. Files are
 * added and reviewed in Brain → Sources, which the screen after setup leads to
 * (`product/0016`).
 */
export function StepReview({ project, mode, goTo }: StepProps) {
  const projectId = project?.id;
  const navigate = useNavigate();

  const { data: phases } = usePhases(projectId);
  const { data: requirements } = useRequirements();
  const jurisdiction = useJurisdiction(project?.country ?? undefined);

  const updateProject = useUpdateProject();
  const replacePhases = useReplacePhases(projectId ?? "");
  const activate = useActivateProject();

  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<number | undefined>(undefined);
  const [flushing, setFlushing] = useState(false);
  const flushes = useRef(new Set<() => Promise<boolean>>());

  useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  const flashSaved = (): void => {
    setSaved(true);
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), 1600);
  };

  const saveBasics = async (patch: ProjectBasicsPatch): Promise<void> => {
    if (!project) return;
    await updateProject.mutateAsync({ id: project.id, patch });
    flashSaved();
  };

  const [phaseError, setPhaseError] = useState<string | null>(null);

  /**
   * Write one field of one phase.
   *
   * The endpoint replaces the whole list, so every inline edit rebuilds the
   * array with a single row changed — that is why this exists rather than each
   * input doing its own `.map`.
   *
   * The order check is the client half of `PhaseInputSchema`, which enforces the
   * same rule server-side. Without it an end date dragged before its start comes
   * back as a raw 400 with the row it belongs to nowhere in the message.
   */
  const commitPhase = async (
    phaseId: string,
    patch: { name?: string; startDate?: string | null; endDate?: string | null },
  ): Promise<void> => {
    if (!phases) return;

    const target = phases.find((p) => p.id === phaseId);
    if (!target) return;

    const nextStart = patch.startDate !== undefined ? patch.startDate : target.startDate;
    const nextEnd = patch.endDate !== undefined ? patch.endDate : target.endDate;
    if (nextStart && nextEnd && nextEnd < nextStart) {
      setPhaseError(
        `“${target.name}” would end before it starts. The dates were not saved.`,
      );
      return;
    }
    setPhaseError(null);

    await replacePhases.mutateAsync(
      phases.map((p) => {
        const row = {
          id: p.id,
          level: p.level,
          name: p.name,
          startDate: p.startDate,
          endDate: p.endDate,
          note: p.note,
          sourceDocumentId: p.sourceDocumentId,
          origin: p.origin,
          editedByUser: p.editedByUser,
        };
        return p.id === phaseId ? { ...row, ...patch, editedByUser: true } : row;
      }),
    );
    flashSaved();
  };

  // Phases and sub-phases (2026-10-03): a phase with sub-phases folds them
  // away, as on Milestones, and says when they run.
  const allPhases = phases ?? NO_PHASES;
  const outline = useFoldedGroups(allPhases, phaseKey);
  const numbers = outlineNumbers(allPhases);
  const subPhases = allPhases.filter((phase) => phase.level > 0).length;

  const saveError = updateProject.error ?? replacePhases.error ?? null;
  const weeks = durationInWeeks(
    project?.startDate ?? null,
    project?.targetHandoverDate ?? null,
  );

  const onComplete = async (): Promise<void> => {
    if (!project) return;
    await activate.mutateAsync(project.id);
    // The screen that says the project is set up, and leads to Sources
    // (2026-10-03). It went to the overview before that; once, to `/project`,
    // which matched no route and dropped the user on the picker.
    navigate(`/projects/${project.id}/setup/done`);
  };

  /**
   * Edit mode's primary: flush, then leave. No `activate`.
   *
   * `activate` is idempotent and would no-op on a live project, so the bug it
   * causes is not a wrong write — it is that `POST /activate` on a live project
   * is a lie about what the button did, against an endpoint whose
   * `setupCompletedAt` unarchive reads back later.
   *
   * By the time this is pressed there is usually nothing left to write: every
   * field saves on blur. The flush is for the case where it is pressed while a
   * debounce is still in flight.
   */
  const onSave = async (): Promise<void> => {
    if (!project) return;
    setFlushing(true);
    let written: boolean[];
    try {
      written = await Promise.all([...flushes.current].map((flush) => flush()));
    } finally {
      setFlushing(false);
    }
    // A flush that failed keeps the user here, for the same reason a save error
    // already on screen disables this button: leaving in the belief a change
    // landed when it did not is the failure this whole step guards against.
    // The mutation's error is on screen by the time this returns.
    if (written.some((ok) => !ok)) return;
    navigate(`/projects/${project.id}/overview`);
  };

  if (!project) return null;

  return (
    <FlushRegistry.Provider value={flushes.current}>
      <StepPanel
        title="Review"
        description="Last look — anything here can be changed after the project is live."
      >
        <div className="flex flex-col gap-8">
          <section>
            <SectionLabel
              className="mb-3"
              trailing={
                saved ? (
                  <span className="flex items-center gap-1 text-ok">
                    <Check size={14} /> Saved
                  </span>
                ) : undefined
              }
            >
              Project basics
            </SectionLabel>

            <dl className="rounded-card border-[1.5px] border-border bg-surface px-5 py-2">
              <EditableRow label="Project name">
                <DebouncedInput
                  value={project.name}
                  ariaLabel="Project name"
                  onCommit={(value) =>
                    value.trim() ? saveBasics({ name: value.trim() }) : undefined
                  }
                />
              </EditableRow>

              <EditableRow label="Country">
                <Select
                  // Undefined rather than null so the trigger shows its
                  // placeholder: a project can reach Review with no country if
                  // step 1 was left on the plan upload alone.
                  value={project.country ?? undefined}
                  onValueChange={(value) =>
                    void saveBasics({ country: value as CountryCode })
                  }
                >
                  <SelectTrigger aria-label="Country" className="text-right">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countryCodes().map((code) => (
                      <SelectItem key={code} value={code}>
                        {countryLabel(code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </EditableRow>

              <EditableRow label="Region">
                <DebouncedInput
                  value={project.region ?? ""}
                  ariaLabel="Region"
                  placeholder="Not set"
                  onCommit={(value) => saveBasics({ region: value.trim() })}
                />
              </EditableRow>

              <EditableRow label="Capacity / spec">
                <DebouncedInput
                  value={project.spec ?? ""}
                  ariaLabel="Capacity or spec"
                  placeholder="Not set"
                  onCommit={(value) => saveBasics({ spec: value.trim() })}
                />
              </EditableRow>

              <EditableRow label="Project start">
                <DebouncedInput
                  value={project.startDate ?? ""}
                  ariaLabel="Project start"
                  type="date"
                  onCommit={(value) =>
                    value ? saveBasics({ startDate: value }) : undefined
                  }
                />
              </EditableRow>

              <EditableRow label="Target handover" last>
                <DebouncedInput
                  value={project.targetHandoverDate ?? ""}
                  ariaLabel="Target handover"
                  type="date"
                  onCommit={(value) =>
                    value ? saveBasics({ targetHandoverDate: value }) : undefined
                  }
                />
              </EditableRow>
            </dl>
            {weeks !== null && (
              <p className="mt-2 text-[0.8rem] text-muted">
                That’s {weeks} weeks from start to target handover.
              </p>
            )}
          </section>

          <section>
            <SectionLabel
              className="mb-3"
              trailing={
                subPhases > 0
                  ? `${allPhases.length - subPhases} ${allPhases.length - subPhases === 1 ? "phase" : "phases"} · ${subPhases} ${subPhases === 1 ? "sub-phase" : "sub-phases"}`
                  : `${allPhases.length} ${allPhases.length === 1 ? "phase" : "phases"}`
              }
            >
              Milestones
            </SectionLabel>
            {(phases?.length ?? 0) === 0 ? (
              <EmptyNote>
                No phases. You can add them from step 2, or after the project is live.
              </EmptyNote>
            ) : (
              <ul className="overflow-hidden rounded-card border-[1.5px] border-border bg-surface">
                {outline.visible.map((index) => {
                  const phase = allPhases[index]!;
                  const group = hasChildren(allPhases, index);
                  const folded = outline.isFolded(phase);
                  const span = group ? subPhaseSpan(allPhases, index) : null;
                  const count = subtreeEnd(allPhases, index) - index - 1;
                  return (
                    <li
                      key={phase.id}
                      className="border-b border-border-inner px-4 py-3 last:border-b-0"
                      style={{ paddingLeft: 16 + phase.level * 24 }}
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="flex w-12 shrink-0 items-center gap-0.5 font-display text-[0.72rem] font-semibold tracking-[0.06em] text-muted">
                          {group ? (
                            <button
                              type="button"
                              onClick={() => outline.toggle(phase.id)}
                              aria-expanded={!folded}
                              aria-label={`${folded ? "Show" : "Hide"} ${count === 1 ? "the sub-phase" : `the ${count} sub-phases`} of ${phase.name}`}
                              className="-ml-1 flex size-6 items-center justify-center rounded-control hover:bg-subtle hover:text-ink"
                            >
                              {folded ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                            </button>
                          ) : null}
                          {numbers[index]}
                        </span>
                        <div className="min-w-0 flex-1">
                          <DebouncedInput
                            value={phase.name}
                            ariaLabel={`Phase name: ${phase.name}`}
                            onCommit={async (value) => {
                              if (!value.trim()) return;
                              await commitPhase(phase.id, { name: value.trim() });
                            }}
                          />
                        </div>
                        {/* Dates are edited in place, like the name beside them.
                            They used to be a static span on a screen whose whole
                            promise is that everything is editable here, so a parse
                            that read the right phase off the wrong row could only
                            be fixed by going back to step 2. */}
                        <div className="flex shrink-0 items-center gap-2">
                          <div className="w-[9.5rem]">
                            <DebouncedInput
                              type="date"
                              value={phase.startDate ?? ""}
                              ariaLabel={`Start date for ${phase.name}`}
                              onCommit={async (value) => {
                                await commitPhase(phase.id, { startDate: value || null });
                              }}
                            />
                          </div>
                          <span aria-hidden className="text-[0.8rem] text-muted">
                            →
                          </span>
                          <div className="w-[9.5rem]">
                            <DebouncedInput
                              type="date"
                              value={phase.endDate ?? ""}
                              ariaLabel={`End date for ${phase.name}`}
                              onCommit={async (value) => {
                                await commitPhase(phase.id, { endDate: value || null });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      {span && <SpanLine span={span} className="mt-1.5 pl-12" />}
                    </li>
                  );
                })}
              </ul>
            )}
            {phaseError && (
              <p
                role="alert"
                className="mt-3 rounded-input border-[1.5px] border-warn/40 bg-warn-bg px-4 py-3 text-[0.85rem] font-semibold text-ink"
              >
                {phaseError}
              </p>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => goTo(STEP.milestones)}
            >
              Edit milestones
            </Button>
          </section>

          <section>
            <SectionLabel className="mb-3">Compliance</SectionLabel>
            <RoleCards
              jurisdiction={jurisdiction.data ?? null}
              loading={jurisdiction.isPending}
              countryName={countryLabel(project.country) ?? ""}
            />
            <p className="mt-2.5 text-[0.8rem] text-muted">
              These come from the country, so change{" "}
              <strong className="font-semibold text-ink-slate">Country</strong> above to
              change them.
            </p>

            {(requirements?.length ?? 0) > 0 && (
              <ul className="mt-4 overflow-hidden rounded-card border-[1.5px] border-border bg-surface">
                {requirements!.map((requirement) => (
                    <li
                      key={requirement.id}
                      className="flex items-center justify-between gap-3 border-b border-border-inner px-4 py-3 last:border-b-0"
                    >
                      <span className="text-[0.88rem] font-semibold text-ink">
                        {requirement.label}
                      </span>
                      <span className="text-[0.8rem] text-muted">
                        {requirement.dataType === "select"
                          ? (requirement.selectOptions ?? []).join(", ")
                          : requirement.dataType}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => goTo(STEP.compliance)}
            >
              Edit info fields
            </Button>
          </section>
        </div>
      </StepPanel>

      {/* The secondary is Back in both modes, not Cancel. Nothing is held
          pending here — every field has already written — so a control called
          Cancel would claim a discard it cannot perform. */}
      <WizardFooter
        onBack={() => goTo(STEP.compliance)}
        onContinue={() => void (mode === "edit" ? onSave() : onComplete())}
        continueLabel={mode === "edit" ? "Save changes" : "Complete setup"}
        busy={mode === "edit" ? flushing : activate.isPending}
        // A save that failed blocks the exit in both modes: leaving having
        // silently dropped the user's last edit is worse than making them
        // retry it.
        continueDisabled={Boolean(saveError)}
        error={
          saveError instanceof Error
            ? `${saveError.message} — fix this before ${
                mode === "edit" ? "saving your changes" : "completing setup"
              }.`
            : activate.error instanceof Error
              ? activate.error.message
              : null
        }
      />
    </FlushRegistry.Provider>
  );
}

function EditableRow({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-4 py-2.5 ${
        last ? "" : "border-b border-border-inner"
      }`}
    >
      <dt className="text-[0.84rem] font-semibold text-muted">{label}</dt>
      <dd className="w-full max-w-[300px]">{children}</dd>
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-card border-[1.5px] border-border bg-surface px-4 py-5 text-[0.86rem] text-muted">
      {children}
    </p>
  );
}

/**
 * An input that saves 500 ms after typing stops.
 *
 * Local state is authoritative while focused so a slow round trip cannot yank
 * characters out from under the user; it re-syncs from the prop only when the
 * field is not being edited.
 */
function DebouncedInput({
  value,
  onCommit,
  ariaLabel,
  type,
  placeholder,
}: {
  value: string;
  onCommit: (value: string) => Promise<void> | undefined;
  ariaLabel: string;
  type?: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  // What the server has already been told, and the promise for it if that
  // write has not landed yet. Comparing against the `value` prop is not enough
  // once anything can flush: blurring dispatches a commit, the prop only
  // catches up when the response arrives, and in between a flush would send the
  // identical value a second time.
  const sent = useRef(value);
  const inFlight = useRef<Promise<boolean> | undefined>(undefined);

  useEffect(() => {
    if (!focused) {
      setDraft(value);
      sent.current = value;
    }
  }, [value, focused]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const commit = (next: string): void => {
    if (next === sent.current) return;
    sent.current = next;
    // The rejection is caught, not swallowed: the mutation's own `error` is
    // what the footer reads, and it disables the primary and names the failure.
    // Catching here stops a failed inline save *also* surfacing as an unhandled
    // rejection, and stops one failed field breaking the flush of the others.
    inFlight.current = Promise.resolve(onCommit(next)).then(
      () => true,
      () => false,
    );
  };

  const schedule = (next: string): void => {
    setDraft(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => commit(next), SAVE_DELAY_MS);
  };

  // Write whatever is pending, on demand. Held in a ref so the registration
  // below can be stable while the closure it runs stays current.
  const flush = useRef<() => Promise<boolean>>(async () => true);
  flush.current = async () => {
    window.clearTimeout(timer.current);
    commit(draft);
    // Nothing was ever committed from this field, so nothing can have failed.
    return (await inFlight.current) ?? true;
  };

  const registry = useContext(FlushRegistry);
  useEffect(() => {
    if (!registry) return;
    const run = (): Promise<boolean> => flush.current();
    registry.add(run);
    return () => {
      registry.delete(run);
    };
  }, [registry]);

  return (
    <Input
      aria-label={ariaLabel}
      type={type}
      value={draft}
      placeholder={placeholder}
      className="text-right"
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        // Flush immediately rather than waiting out the debounce — a user who
        // tabs away and presses the footer's primary must not lose the last
        // edit.
        window.clearTimeout(timer.current);
        commit(draft);
      }}
      onChange={(e) => schedule(e.target.value)}
    />
  );
}

export { Loader2 };
